import * as React from 'react';
import './ReportMenuComponent.scss';
import bind from 'bind-decorator';
import { Alert, Button, ButtonGroup, Col, Dropdown, Form, Row } from 'react-bootstrap';
import ReactGA from 'react-ga';
import { FormInterface } from '../../../Interfaces/Forms/FormsInterface';
import { ClientPersistInterface } from '../../../Interfaces/ClientPersistInterface';
import { FiltersMenuInterface } from '../../../Interfaces/FiltersMenuInterface';
import { getLocalization, globalWindow } from '../../../global/global'; // globalWindow
import GenericModal from '../../Modals/GenericModal';
import { getQueryParams } from '../../../utils/utils';
import { ConfirmationModal } from '../../Modals/ConfirmationModal';
import ReportQuestionSelector from './ReportQuestionSelector';
import SaveReportButton from './SaveReportButton';
import { getLegendQuestions, getSubgroupOptions } from './utils';

interface Props {
  forms: FormInterface[];
  clientPersist: ClientPersistInterface;
  reports: any;
  saveReportTemplate: (reportName: string, parameters: string, reportId: string) => void;
  deleteReport: (reportId: string) => void;
  filtersMenu: FiltersMenuInterface;
}

interface State {
  showModal: boolean;
  confirmDelete: boolean;
  form: string;
  validate: boolean;
  chartType: string;
  format: string;
  applylocationfilters: string;
  applydatefilters: string;
  applyuserfilters: string;
  includelocation: string;
  excludenull: string;
  legendQuestion: string;
  subgrpQuestion: string;
  criteriaQuestion: string;
  type: string;
  rowquestion: string[];
  questions: string[];
  reportId: string;
  reportName: string;
  showDataPointsHorizontally: string;
  dataPerGroup: string;
}

export default class ReportMenuComponent extends React.Component<Props, State> {

  constructor(props) {
    super(props);
    this.state = this.getInitialState();
  }

  private getInitialState() {
    return {
      showModal: false,
      confirmDelete: false,
      form: '',
      validate: false,
      chartType: '',
      format: 'xlsx',
      applylocationfilters: '0',
      applydatefilters: '0',
      applyuserfilters: '0',
      includelocation: '0',
      excludenull: '0',
      legendQuestion: '',
      subgrpQuestion: '',
      criteriaQuestion: '',
      type: 'SUM',
      rowquestion: [],
      questions: [],
      reportId: '',
      reportName: '',
      showDataPointsHorizontally: '',
      dataPerGroup: '',
    };
  }

  @bind
  private showReportModal(visible) {
    if (!visible) {
      this.setState(this.getInitialState());
    } else {
      this.setState({ showModal: visible });
    }
  }

  @bind
  private onSelectChange(e) {
    const newState: Partial<State> = {};
    newState[e.target.name] = e.target.value;
    this.setState({...this.state, ...newState});
  }

  @bind
  private onMultipleSelectChange(e) {
    const newState: Partial<State> = {};

    let i = 0;
    const options = e.target.options;
    const selectedOptions: string[] = [];
    while (i < options.length) {
      if (options[i].selected) {
        selectedOptions.push(options[i].value);
      }
      i++;
    }
    newState[e.target.name] = selectedOptions;
    this.setState({...this.state, ...newState});
  }

  @bind
  private onCheckChange(e) {
    const newState: Partial<State> = {};
    newState[e.target.name] = e.target.checked ? e.target.value : '0';
    if (e.target.name === 'chartType') {
      newState.type = 'SUM';
      newState.showDataPointsHorizontally = '';
      newState.dataPerGroup = '';
    }
    if (e.target.name === 'chartType' && e.target.value !== -1) {
      newState.dataPerGroup = '';
    }
    this.setState({...this.state, ...newState});
  }

  @bind
  private onQuestionsChange(questions) {
    this.setState({ questions });
  }

  // @ts-ignore
  @bind
  private getLegendOptions(form: FormInterface) {
    const { chartType } = this.state;
    const { clientPersist } = this.props;
    let options: (JSX.Element | null)[] = [];

    if (chartType === '-1') {
      if (form.type !== 'TABLE') {
        options.push(<option value={'user_id'}>User</option>);
      }
      if (form.isChild) {
        options.push(<option value={'parent_name'}>Parent form name</option>);
        options.push(<option value={'parent_location1'}>Parent Location Level 1</option>);
        options.push(<option value={'parent_location2'}>Parent Location Level 2</option>);
        options.push(<option value={'parent_location3'}>Parent Location Level 3</option>);
        options.push(<option value={'parent_location4'}>Parent Location Level 4</option>);
      }
    }
    if (form.type === 'POI' && !form.isChild) {
      const locationLabels = clientPersist.locationLabels;
      let i = 1;
      for (const label of locationLabels) {
        if (label) {
          options.push(<option value={i}>{label}</option>);
        }
        i++;
      }
    }

    if (form.questionPage) {
      for (const page of form.questionPage) {
        options = options.concat(getLegendQuestions(page.question || [], page.pageNumber, chartType));
      }
    }
    if (form.question) {
      options = options.concat(getLegendQuestions(form.question, '', chartType));
    }
    return options;
  }

  @bind
  private getSubgroupOption(form) {
    const { chartType } = this.state;
    const { clientPersist } = this.props;
    let options: (JSX.Element | null)[] = [];

    if (chartType === '-1' || chartType === '-2') {
      if (form.type !== 'TABLE') {
        if (!form.isChild) {
          const locationLabels = clientPersist.locationLabels;
          let i = 1;
          for (const label of locationLabels) {
            if (label) {
              options.push(<option value={i}>{label}</option>);
            }
            i++;
          }
        }
      }
    }
    if (form.questionPage) {
      for (const page of form.questionPage) {
        options = options.concat(getSubgroupOptions(page.question || [], page.pageNumber, chartType));
      }
    }
    if (form.question) {
      options = options.concat(getSubgroupOptions(form.question, '', chartType));
    }
    return options;
  }

  @bind
  private getModalContent() {
    const { forms } = this.props;
    const error = this.state.validate ? 'has-error' : '';
    const { chartType, type, legendQuestion, showDataPointsHorizontally } = this.state;
    const form = forms.find(f => f.ref === this.state.form);

    const getError = () => {
      if (this.state.validate) {
        return (
          <Alert variant={'danger'}>
            {getLocalization('reportMissingFields')}
          </Alert>
        );
      }
      return null;
    };

    const getChartLayout = () => {
      return chartType === '-1' ? (
        <>
          <Form.Group className="mb-3">
            <Form.Check
              id={`report-modal-show-datapoints-horizontally`}
              type="checkbox"
              name="showDataPointsHorizontally"
              label={getLocalization('showDataPointsHorizontally')}
              value="1"
              checked={this.state.showDataPointsHorizontally === '1'}
              onChange={this.onCheckChange}
            />
          </Form.Group>
          {(type === 'SUM' || type === 'AVG') && (
            <Form.Group className="mb-3">
              <Form.Check
                id={`report-modal-show-data-per-group`}
                type="checkbox"
                name="dataPerGroup"
                label={getLocalization('dataPerGroup')}
                value="1"
                checked={this.state.dataPerGroup === '1'}
                disabled={showDataPointsHorizontally === '1'}
                onChange={this.onCheckChange}
              />
            </Form.Group>
          )}
        </>
      ) : null;
    };

    const isInvalid = (name: string) => {
      return this.state.validate && this.state[name] === '';
    };
    /* const hasError = (value) => {
      return value === '' && error;
    };*/

    return (
      <div>
        {getError()}
        <Form.Group className="mb-3">
          <Form.Control
            as="select"
            name="form"
            onChange={this.onSelectChange}
            value={this.state.form}
            isInvalid={isInvalid('form')}
          >
            <option value="">{getLocalization('selone')}</option>
            {forms.map(f => {
              const prefix = f.type === 'TABLE' ? 'Table:' :
                f.type === 'TASKFORM' ? 'Taskform:' :
                  f.isChild && f.type === 'POI' ? 'Subform:' : '';
              return (<option value={f.ref} key={f.id}>{`${prefix}${f.name}`}</option>);
            })}
          </Form.Control>
        </Form.Group>
        <Row>
          <Col sm={4}>
            <Form.Group className="mb-3">
              <Form.Label>{getLocalization('selectcharttype')}</Form.Label>
              <Form.Check
                required={this.state.validate}
                id={`report-modal-report-type-table`}
                type="radio"
                name="chartType"
                label={getLocalization('reportsTable')}
                value="-1"
                checked={this.state.chartType === '-1'}
                onChange={this.onCheckChange}
                isInvalid={isInvalid('chartType')}
              />
              <Form.Check
                required={this.state.validate}
                id={`report-modal-report-type-cross-table`}
                type="radio"
                name="chartType"
                label={getLocalization('reportsCrosstable')}
                value="-2"
                checked={this.state.chartType === '-2'}
                onChange={this.onCheckChange}
                isInvalid={isInvalid('chartType')}
              />
            </Form.Group>
          </Col>
          <Col sm={4}>
            <Form.Group className="mb-3">
              <Form.Label>{getLocalization('reportsFormat')}</Form.Label>
              <Form.Check
                id={`report-modal-report-format-excel`}
                type="radio"
                name="format"
                label={getLocalization('reportsExcel')}
                value="xlsx"
                checked={this.state.format === 'xlsx'}
                onChange={this.onCheckChange}
              />
              <Form.Check
                id={`report-modal-report-format-word`}
                type="radio"
                name="format"
                label={getLocalization('reportsWord')}
                value="docx"
                checked={this.state.format === 'docx'}
                onChange={this.onCheckChange}
              />
              <Form.Check
                id={`report-modal-report-format-pdf`}
                type="radio"
                name="format"
                label={getLocalization('reportsPdf')}
                value="pdf"
                checked={this.state.format === 'pdf'}
                onChange={this.onCheckChange}
              />
            </Form.Group>
          </Col>
          <Col sm={4}>
            <Form.Check
              id={`report-modal-report-apply-user-filters`}
              type="checkbox"
              name="applyuserfilters"
              label={getLocalization('applycurrentuserfilters')}
              value="1"
              checked={this.state.applyuserfilters === '1'}
              onChange={this.onCheckChange}
            />
            <Form.Check
              id={`report-modal-report-apply-date-filters`}
              type="checkbox"
              name="applydatefilters"
              label={getLocalization('applycurrentdatefilters')}
              value="1"
              checked={this.state.applydatefilters === '1'}
              onChange={this.onCheckChange}
            />
            <Form.Check
              id={`report-modal-report-apply-location-filters`}
              type="checkbox"
              name="applylocationfilters"
              label={getLocalization('applycurrentlocationfilters')}
              value="1"
              checked={this.state.applylocationfilters === '1'}
              onChange={this.onCheckChange}
            />
          </Col>
        </Row>
        {form && form.hasLocationHierarchy && (
          <Form.Group className="mb-3">
            <Form.Check
              id={`report-modal-report-show-location-names`}
              type="checkbox"
              name="includelocation"
              label={getLocalization('showlocationnames')}
              value="1"
              checked={this.state.includelocation === '1'}
              onChange={this.onCheckChange}
            />
          </Form.Group>
        )}
        {
          // Table & Crosstab
        }
        <Row>
          <Col sm={6}>
            <Form.Group className="mb-3">
              <Form.Label>{this.state.chartType === '-1' ? getLocalization('selectgroupby')
                : getLocalization('selectcolumngroup')
              }</Form.Label>
              <Form.Control
                as="select"
                name="legendQuestion"
                onChange={this.onSelectChange}
                value={this.state.legendQuestion}
              >
                <option value="">{getLocalization('selone')}</option>
                {form && chartType ? this.getLegendOptions(form) : []}
              </Form.Control>
            </Form.Group>
          </Col>
          { this.state.legendQuestion !== '' && this.state.chartType === '-1' &&
          (
            <Col sm={6}>
              <Form.Group className="mb-3">
                <Form.Label>{getLocalization('selectsubgroup')}</Form.Label>
                <Form.Control
                  as="select"
                  name="subgrpQuestion"
                  onChange={this.onSelectChange}
                  value={this.state.subgrpQuestion}
                  disabled={showDataPointsHorizontally === '1'}
                >
                  <option value="">{getLocalization('selone')}</option>
                  {form ?
                    this.getSubgroupOption(form)
                      .filter( el => el && `${el.props.value}` !== this.state.legendQuestion) :
                    []
                  }
                </Form.Control>
              </Form.Group>
            </Col>
          )
          }
        </Row>
        { // Table
          chartType === '-1' && (
            <Form.Group className="mb-3">
              <Form.Label>{getLocalization('excludecriteria')}</Form.Label>
              <Form.Control
                as="select"
                name="criteriaQuestion"
                onChange={this.onSelectChange}
                value={this.state.criteriaQuestion}
              >
                <option value="">{getLocalization('selone')}</option>
                {form ? this.getSubgroupOption(form) : []}
              </Form.Control>
            </Form.Group>
          )
        }
        { // Cross Tab
          chartType === '-2' && (
            <Form.Group className="mb-3">
              <Form.Label>{getLocalization('selrowgroup')}</Form.Label>
              <Form.Control
                as="select"
                name="rowquestion"
                onChange={this.onMultipleSelectChange}
                // @ts-ignore
                value={this.state.rowquestion}
                multiple
                disabled={this.state.legendQuestion === ''}
              >
                <option value="">{getLocalization('selone')}</option>
                {form && chartType ?
                  // @ts-ignore
                  this.getLegendOptions(form).filter( el => el && el.props.value !== this.state.legendQuestion) : []}
              </Form.Control>
            </Form.Group>
          )
        }

        {
          // Table & cross tab
        }
        <Form.Group className="mb-3">
          <Form.Label>{getLocalization('selcalcs')}</Form.Label>
          <Form.Control
            as="select"
            name="type"
            onChange={this.onSelectChange}
            value={this.state.type}
            disabled={legendQuestion === '' || (showDataPointsHorizontally === '1' && legendQuestion !== '')}
          >
            <option value="">{getLocalization('selone')}</option>
            <option value="SUM">{getLocalization('reportsSum')}</option>
            <option value="AVG">{getLocalization('reportsAverage')}</option>
            {chartType === '-2' && (<option value="PERCENT">{getLocalization('reportsPercentage')}</option>)}
            {chartType === '-1' && (<option value="NONE">{getLocalization('reportsNone')}</option>)}
          </Form.Control>
        </Form.Group>
        {getChartLayout()}
        <Form.Group className="mb-3">
          <div className={`form-group ${this.state.questions.length === 0 && error}`}>
            {getLocalization('movequestions')}
          </div>
          <ReportQuestionSelector
            form={form}
            chartType={chartType}
            questions={this.state.questions}
            showDataPointsHorizontally={showDataPointsHorizontally}
            onQuestionsChange={this.onQuestionsChange}
          />
        </Form.Group>
      </div>
    );
  }

  @bind
  private createReport() {
    this.setState({ showModal: true });
    const { clientPersist } = this.props;
    if (clientPersist.payingCustomer === 'yes') {
      ReactGA.event({
        category: 'module-selection',
        action: `report`,
        label: `${clientPersist.instance}`
      });
    }
  }

  @bind
  private generateReport() {
    const { clientPersist } = this.props;
    const { form, includelocation, chartType, type } = this.state;
    if (!this.parametersValid()) {
      return;
    }
    const params: string[] = [ `user_id=${clientPersist.user_id}`,
      `lang=${clientPersist.lang}`, `key=${globalWindow.pwrd}`, `userid=${globalWindow.userName}`
    ];

    const extraParams = this.getReportParameters('url');

    const url = `/json/app/jasper/${form}/${includelocation}/1/${chartType}/${type}?`
      + `${params.join('&')}&${extraParams}`;
    globalWindow.open(url, '_blank');
  }

  @bind
  private parametersValid() {
    const {
      form, chartType, questions, legendQuestion, rowquestion
    } = this.state;
    if (form === '' || chartType === '' || questions.length === 0) {
      this.setState({ validate: true });
      return false;
    }
    if (chartType === '-2') {
      if (legendQuestion === '' || rowquestion.length === 0) {
        this.setState({ validate: true });
        return false;
      }
    }
    return true;
  }

  @bind
  private getReportParameters() {
    const urlParams: string[] = [];
    const { clientPersist, filtersMenu } = this.props;
    const state = this.state;
    const params = {
      questions: state.questions.join(','),
      locs: '',
      levels: '',
      users: '',
      fromdate: '',
      todate: '',
      timeinfo: 0,
      userinfo: 0,
      legendid: state.legendQuestion,
      reportType: 0,
      locationLevels: '-1',
      combinenumericfields: 0,
      timeseriestype: '',
      locationLevelDataFilter: '',
      orientation: 1,
      rowquestionid: state.rowquestion.join(','),
      groupid: clientPersist.groupId,
      format: state.format,
      excludenull: state.excludenull,
      subgroup: state.subgrpQuestion,
      criteriafield: state.criteriaQuestion,
      dataPerGroup: state.dataPerGroup,
      dateFilterBy: '',
      showDataPointsHorizontally: state.showDataPointsHorizontally
    };

    if (this.state.applylocationfilters === '1') {
      const locs = filtersMenu.selectedLocations.map(l => l.key).join(',');
      const levels = filtersMenu.selectedLocations.map(l => Number(l.level) + 1).join(',');
      urlParams.push(`locs=${locs}`);
      urlParams.push(`levels=${levels}`);
      params.locs = locs;
      params.levels = levels;
    }
    if (this.state.applyuserfilters === '1') {
      const users = filtersMenu.selectedUsers.map(u => u.id).join(',');
      urlParams.push(`users=${users}`);
      params.users = users;
    }
    if (this.state.applydatefilters === '1') {
      urlParams.push(`fromdate=${filtersMenu.selectedDates.from}`);
      urlParams.push(`todate=${filtersMenu.selectedDates.to}`);
      urlParams.push(`dateFilterBy=${filtersMenu.selectedDates.filterBy}`);
      params.fromdate = filtersMenu.selectedDates.from || '';
      params.todate = filtersMenu.selectedDates.to || '';
      params.dateFilterBy = filtersMenu.selectedDates.filterBy;
    }

    urlParams.push(`questions=${params.questions}`);
    urlParams.push(`timeinfo=${params.timeinfo}`);
    urlParams.push(`userinfo=${params.userinfo}`);
    urlParams.push(`legendid=${params.legendid}`);
    urlParams.push(`reportType=${params.reportType}`);
    urlParams.push(`locationLevels=${params.locationLevels}`);
    urlParams.push(`orientation=${params.orientation}`);
    urlParams.push(`rowquestionid=${params.rowquestionid}`);
    urlParams.push(`groupid=${params.groupid}`);
    urlParams.push(`format=${params.format}`);
    urlParams.push(`excludenull=${params.excludenull}`);
    urlParams.push(`subgroup=${params.subgroup}`);
    urlParams.push(`criteriafield=${params.criteriafield}`);
    urlParams.push(`dataPerGroup=${params.dataPerGroup}`);
    urlParams.push(`showDataPointsHorizontally=${params.showDataPointsHorizontally}`);
    urlParams.push(`combinenumericfields=0`);
    urlParams.push(`locationLevelDataFilter=`);
    return urlParams.join('&');
  }

  @bind
  private saveReportTemplate(reportName: string) {
    const parameters = this.getReportParameters('save');
    const {
      form, includelocation, chartType, type, reportId
    } = this.state;
    const url = `/json/app/jasper/${form}/${includelocation}/1/${chartType}/${type}?XXX${parameters}`;
    this.props.saveReportTemplate(reportName, url, reportId);
  }

  @bind
  private editReport(report) {
    const state: Partial<State> = {};
    const url = report.url;
    const parameters = getQueryParams(url.substring(url.indexOf('?')));
    state.form = report.form;
    state.chartType = report.report_type === 'Table' ? '-1' : report.report_type === 'Crosstab' ? '-2' : '';
    state.type = url.substring(url.lastIndexOf('/') + 1, url.indexOf('?'));
    state.reportId = report.report_id;
    state.format = parameters.format;
    state.legendQuestion = parameters.legendid;
    state.questions = parameters.questions.split(',');
    state.subgrpQuestion = parameters.subgroup;
    state.excludenull = parameters.excludenull;
    state.rowquestion = parameters.rowquestionid.split(',');
    state.criteriaQuestion = parameters.criteriafield ? parameters.criteriafield : '';
    state.applydatefilters = parameters.fromdate || parameters.todate ? '1' : '0';
    state.applylocationfilters = parameters.locs ? '1' : '0';
    state.applyuserfilters = parameters.users ? '1' : '0';
    state.dataPerGroup = parameters.dataPerGroup;
    state.includelocation = report.show_locations === 'Yes' ? '1' : '0';
    state.showModal = true;
    state.reportName = report.report_name;
    this.setState((prevState) => ({...prevState, ...state}));
  }

  @bind
  private confirmDelete(report) {
    this.setState({ confirmDelete: true, reportId: report.report_id });
  }

  @bind
  private deleteReport() {
    const { reportId } = this.state;
    this.props.deleteReport(reportId);
    this.showReportModal(false);
  }

  @bind
  private getSaveReportButton() {
    return [(
      <SaveReportButton
        key={`report-save-btn`}
        saveReportTemplate={this.saveReportTemplate}
        parametersValid={this.parametersValid}
        reportName={this.state.reportName}
      />
    )
    ];
  }

  @bind
  private applyFilters(url) {
    const parameters = getQueryParams(url.substring(url.indexOf('?')));
    const { filtersMenu, clientPersist } = this.props;
    if (parameters.locs) {
      const locs = filtersMenu.selectedLocations.map(l => l.key).join(',');
      const levels = filtersMenu.selectedLocations.map(l => Number(l.level) + 1).join(',');
      parameters.locs = locs;
      parameters.levels = levels;
      // urlParams.push(`locs=${locs}`);
      // urlParams.push(`levels=${levels}`);
    }
    parameters.user_id = clientPersist.user_id || '';
    parameters.key = globalWindow.pwrd;
    parameters.userid = globalWindow.userName;
    parameters.lang = clientPersist.lang;
    const keys = Object.keys(parameters);
    const urlParams: string[] = [];
    for (const key of keys) {
      urlParams.push(`${key}=${parameters[key]}`);
    }
    const finalUrl = url.substring(0, url.indexOf('?') + 1);
    globalWindow.open(finalUrl + urlParams.join('&'), '_blank');
  }

  @bind
  private getReports() {
    const { reports } = this.props;
    if (reports) {
      return reports.map((report) => {
        return (
          <Dropdown.Item key={`report_menu_${report.report_id}`} eventKey="1">
            {(
              <div className="report-name">
                <div className="pull-left" onClick={() => this.applyFilters(report.url)}>
                  {report.report_name}
                </div>
                <div className="pull-right">
                  <i className="fa fa-trash report-action-icon" onClick={() => this.confirmDelete(report)}/>
                  <i className="fa fa-pencil report-action-icon" onClick={() => this.editReport(report)}/>
                </div>
              </div>
            )}
          </Dropdown.Item>
        );
      });
    }
    return null;
  }

  public render(): JSX.Element {
    const modal = this.state.showModal ? (
      <GenericModal
        visible={this.state.showModal}
        body={this.getModalContent()}
        onConfirm={this.generateReport}
        confirmText={getLocalization('generateReport')}
        title={getLocalization('reportsIntro')}
        cancel={() => this.showReportModal(false)}
        cancelText={getLocalization('cancel')}
        extraButtons={this.getSaveReportButton()}
        dialogClassName="report-menu-modal"
      />
    ) : null;
    const deleteModal = this.state.confirmDelete ? (
      <ConfirmationModal
        visible={this.state.confirmDelete}
        onClose={() => this.showReportModal(false)}
        onConfirm={this.deleteReport}
        localizations={{
          cancel: getLocalization('cancel'),
          confirm: getLocalization('ok'),
          confirmStyle: 'danger',
          header: (<label>{getLocalization('confirm')}</label>),
          body: (<p>{getLocalization('reportConfirmDelete')}</p>)
        }}
      />
    ) : null;
    return (
      <React.Fragment>
        {modal}
        {deleteModal}
        <ButtonGroup>
          <Dropdown
            id={'reports-btn'}
            align={'end'}
          >
            <Dropdown.Toggle size={'sm'} id="report-dropdown-menu">
              <i className="fa fa-tasks" />
              <span className="d-none d-md-inline ms-1">{` ${getLocalization('report')}`}</span>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {this.getReports()}
            </Dropdown.Menu>
          </Dropdown>
          <Button
            title={getLocalization('reports')}
            onClick={this.createReport}
            size={'sm'}
            id={'add-report-btn'}
          >
            <i className="fa fa-plus" />
          </Button>
        </ButtonGroup>
      </React.Fragment>
    );
  }
}

/*
<div className="form-group">
  <div className="checkbox default">
    <label>
      <input
        type="checkbox"
        name="excludenull"
        value="1"
        onChange={this.onCheckChange}
        checked={this.state.excludenull === '1'}
      />
      {getLocalization('excludenull')}
    </label>
  </div>
</div>*/
